import { useQuery } from "@tanstack/react-query";

interface PolymarketMarketData {
  slug: string;
  question: string;
  description: string | null;
  image: string | null;
  endDate: string | null;
  closed: boolean;
  active: boolean;
  acceptingOrders: boolean;
  clobTokenIds: string | null;
  outcomes: string[];
  outcomePrices: number[];
  volume: number;
  liquidity: number;
  orderPriceMinTickSize: number;
  orderMinSize: number;
  enableOrderBook: boolean;
  negRisk: boolean;
  priceToBeat?: number | null;
  resolutionSource?: string | null;
  /** Event slug of the parent Polymarket event. Used to route /market → /event. */
  parentEventSlug?: string | null;
}

class MarketNotFoundError extends Error {
  status = 404;
  constructor() {
    super("Market not found");
  }
}

type Source = "polymarket" | "kalshi" | "predict" | "hyperliquid";

/**
 * Detect a market's source from its slug. Mirrors the inline logic used in
 * `PolymarketMarketCard` so seed/prefetch call sites and the consuming
 * `useMarketData` hook always agree on the queryKey shape — otherwise the
 * 3-element `["market", slug, source]` key in the hook never matches a
 * 2-element seed and every card refetches on mount, flooding the backend.
 */
export function detectMarketSource(slug: string): Source {
  const lower = slug.toLowerCase();
  if (lower.startsWith("kx")) return "kalshi";
  if (/^\d+$/.test(slug)) return "predict";
  if (/-q-\d+-o\d+-(yes|no)$/i.test(slug)) return "hyperliquid";
  return "polymarket";
}

/** Build the canonical queryKey for `useMarketData` — used by seeders. */
export function marketDataQueryKey(slug: string, source?: Source) {
  return ["market", slug, source ?? detectMarketSource(slug)] as const;
}

async function fetchMarketData(slug: string, source?: Source): Promise<PolymarketMarketData> {
  const isKalshi = source === "kalshi" || slug.toLowerCase().startsWith("kx");
  const isHyperliquid = source === "hyperliquid";

  if (isHyperliquid) {
    // Hyperliquid markets aren't in Polymarket's catalog — going to
    // /api/polymarket/market/<slug> here returns 404 and the card falls
    // back to "Market archived" even on live HL outcomes. Hit the HL
    // route instead and shape its response into the same contract.
    const res = await fetch(`/api/markets/hyperliquid/${slug}`);
    if (res.status === 404) throw new MarketNotFoundError();
    if (!res.ok) throw new Error("Failed to fetch market data");
    const raw = await res.json();
    const m = raw.market || {};
    const yesPrice = Number(m.midpoint_price ?? 0.5) || 0.5;
    return {
      slug: m.slug,
      question: m.question_name || m.title || "",
      description: m.question_description || null,
      image: null,
      endDate: m.end_time || null,
      closed: m.status === "settled",
      active: m.status === "active",
      acceptingOrders: m.status === "active",
      clobTokenIds: null,
      outcomes: ["Yes", "No"],
      outcomePrices: [yesPrice, Math.max(0, Math.min(1, 1 - yesPrice))],
      volume: Number(m.daily_volume_usd ?? 0) || 0,
      liquidity: Number(m.open_interest_usd ?? 0) || 0,
      orderPriceMinTickSize: 0.001,
      orderMinSize: 1,
      enableOrderBook: true,
      negRisk: false,
      // HL slugs ARE the event slug — pass them through so the card's
      // YES/NO handlers can route to /hyperliquid/event/<slug> via the
      // same parentEventSlug code path Polymarket uses.
      parentEventSlug: m.slug || null,
    };
  }

  if (isKalshi) {
    const res = await fetch(`/api/markets/kalshi/${slug}`);
    if (res.status === 404) throw new MarketNotFoundError();
    if (!res.ok) throw new Error("Failed to fetch market data");
    const raw = await res.json();
    const km = raw.market;
    return {
      slug: km.slug,
      question: km.eventTitle || km.title,
      description: km.description || null,
      image: km.image || null,
      endDate: km.endDate || null,
      closed: km.status === "finalized" || km.status === "settled",
      active: km.status === "open",
      acceptingOrders: km.status === "open",
      clobTokenIds: null,
      outcomes: ["Yes", "No"],
      outcomePrices: [km.yesPrice || 0.5, km.noPrice || 0.5],
      volume: km.volume || 0,
      liquidity: km.liquidity || 0,
      orderPriceMinTickSize: 0.01,
      orderMinSize: 1,
      enableOrderBook: true,
      negRisk: false,
    };
  }

  // Polymarket.
  //
  // Do NOT attempt a "base slug" retry on 404. Polymarket slugs are stable —
  // the trailing digits in e.g. `bitcoin-up-or-down-on-april-13-2026` or
  // `sol-updown-15m-1768472100` are part of the canonical slug, not a variable
  // suffix. The old retry stripped those digits and produced a second 404
  // against an invented slug, doubling the network traffic on every archived
  // market shown in the activity feed.
  const res = await fetch(`/api/polymarket/market/${slug}`);
  if (res.status === 404) throw new MarketNotFoundError();
  if (!res.ok) throw new Error("Failed to fetch market data");
  return res.json();
}

/**
 * Hook to fetch and cache market data using TanStack Query.
 * Automatically deduplicates concurrent requests for the same slug
 * and shares cached data across all components using the same slug.
 */
export function useMarketData(
  slug: string | undefined,
  options?: { enabled?: boolean; source?: Source },
) {
  // queryKey carries the source so two cards rendering the same slug
  // under different sources (e.g. a future Polymarket / HL slug
  // collision) can't poison each other's cache entry.
  return useQuery<PolymarketMarketData, Error>({
    queryKey: ["market", slug, options?.source ?? null],
    queryFn: () => fetchMarketData(slug!, options?.source),
    enabled: !!slug && (options?.enabled !== false),
    staleTime: 5 * 60_000,  // 5min — market metadata rarely changes mid-session
    gcTime: 30 * 60_000,   // 30min — activity feeds revisit the same archived slugs across navigations
    retry: false,        // Don't auto-retry (404 = archived market)
    // Without this, every remount of a card on a 404'd slug re-fires the
    // request even though the failure is in cache. Profile activity feeds
    // are full of resolved markets that 404 forever — refetching on each
    // visit floods the console with the same 404s every session.
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export type { PolymarketMarketData };
export { MarketNotFoundError };
